// src/hooks/useExamRegistrationsPage.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';

/**
 * Server-side pagination for Exam Registrations using a view that
 * includes flattened names + child aggregates.
 *
 * Params:
 *  - page, pageSize
 *  - search: matches student_name OR exam_session_name
 *  - status: 'all' | 'Registered' | 'Deferred' | 'Cancelled'
 *  - sessionId: 'all' | UUID
 *
 * Returns:
 *  { rows, count, page, pageSize, isLoading, error, refetch, softDeleteRegistration }
 */
export function useExamRegistrationsPage({
  page,
  pageSize,
  search = '',
  status = 'all',
  sessionId = 'all',
}) {
  const qc = useQueryClient();
  const from = Math.max(0, (Number(page) - 1) * Number(pageSize));
  const to = from + Number(pageSize) - 1;

  const queryKey = ['exam_regs_page', { page, pageSize, search, status, sessionId }];

  const queryFn = async () => {
    const hasSearch = Boolean(search?.trim());
    const safe = (search || '').trim().replaceAll(',', ' ').replaceAll('(', ' ').replaceAll(')', ' ');

    let q = supabase
      .from('exam_registrations_list_v') // view defined below
      .select(
        `
        id,
        student_id,
        student_name,
        exam_session_id,
        exam_session_name,
        registration_status,
        venue,
        total_fee_paid,
        payment_date,
        remarks,
        created_at,
        updated_at,
        subjects_total,
        reg_registered,
        reg_deferred,
        reg_cancelled,
        res_pending,
        res_passed
      `,
        { count: 'exact' }
      )
      .eq('is_deleted', false);

    if (status && status !== 'all') q = q.eq('registration_status', status);
    if (sessionId && sessionId !== 'all') q = q.eq('exam_session_id', sessionId);

    if (hasSearch) {
      const pattern = `*${safe}*`;
      // OR across plain columns in the view (stable)
      q = q.or(`student_name.ilike.${pattern},exam_session_name.ilike.${pattern}`);
    }

    // Sort: student ASC, then session start DESC (proxied via updated_at DESC as fallback)
    q = q
      .order('student_name', { ascending: true, nullsFirst: true })
      .order('updated_at', { ascending: false })
      .range(from, to);

    const { data, count, error } = await q;
    if (error) throw error;

    return {
      rows: data || [],
      count: count || 0,
      page,
      pageSize,
    };
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn,
    keepPreviousData: true,
    staleTime: 30_000,
  });

  // Soft delete parent + children
  const { mutateAsync: softDeleteRegistration } = useMutation({
    mutationFn: async (id) => {
      if (!id) throw new Error('Missing registration id');

      const { error: pErr } = await supabase
        .from('student_exam_session')
        .update({ is_deleted: true })
        .eq('id', id);
      if (pErr) throw pErr;

      const { error: cErr } = await supabase
        .from('student_exam_info')
        .update({ is_deleted: true })
        .eq('session_registration_id', id);
      if (cErr) throw cErr;

      return { success: true };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exam_regs_page'] });
    },
  });

  return {
    rows: data?.rows ?? [],
    count: data?.count ?? 0,
    page,
    pageSize,
    isLoading,
    error: error?.message || null,
    refetch,
    softDeleteRegistration,
  };
}
